<script setup>
import { nextTick } from 'vue'
import { onClickOutside } from '@vueuse/core'
import UserMenu from '~/components/Common/UserMenu.vue'

const route = useRoute()
const router = useRouter()
const { isOpen, close } = useMobileMenu()
const { can, actions, subjects } = useRolePermissions()
const { isPromotionSection } = usePromotionNavigation()
const { open: openSubmenu } = usePromotionSubmenu()
const configStore = useConfigStore()
const authStore = useAuthStore()

const appName = computed(() => configStore.appName)

const menuRef = ref(null)

onClickOutside(menuRef, () => {
  if (isOpen.value) {
    close()
  }
})

// Cerrar menú al navegar
router.afterEach(() => {
  close()
})

const mainMenuConfig = [
  { name: 'Dashboard', icon: 'heroicons:home', path: '/', permission: [actions.VIEW, subjects.DASHBOARD] },
  {
    name: 'Ofertas',
    icon: 'material-symbols:sell',
    path: '/quotes',
    permission: [actions.LIST, subjects.BUDGET_QUOTE],
    roleOnly: ['contractor_admin', 'contractor_user']
  },
  {
    name: 'Promociones',
    icon: 'heroicons:building-office',
    path: '/promotions',
    permission: [actions.LIST, subjects.PROMOTIONS],
    isPromotionOverlay: true
  },
  { name: 'Central de Compras', icon: 'mdi:shopping', path: '/product-items', permission: [actions.LIST, subjects.PRODUCT_ITEMS] },
  { name: 'Empresas', icon: 'material-symbols:handshake', path: '/companies', permission: [actions.LIST, subjects.COMPANIES] },
  { name: 'Usuarios', icon: 'heroicons:user-group', path: '/users', permission: [actions.LIST, subjects.USERS] },
  {
    name: 'Invitaciones',
    icon: 'mdi:email-fast-outline',
    path: '/invitations',
    permission: [actions.LIST, subjects.USER_INVITATION],
    hideForRoles: ['contractor_admin', 'contractor_user']
  },
  { name: 'Configuración', icon: 'heroicons:cog-6-tooth', path: '/settings', permission: null, roleOnly: ['architect', 'contractor_admin'] }
]

const menuItems = computed(() => {
  return mainMenuConfig.filter(item => {
    if (item.roleOnly && !item.roleOnly.includes(authStore.role)) return false
    if (item.hideForRoles?.includes(authStore.role)) return false
    if (!item.permission) return true
    return can(item.permission[0], item.permission[1])
  })
})

const isMenuItemActive = (itemPath) => {
  const currentPath = route.path
  if (itemPath === '/') {
    return currentPath === '/'
  }
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/')
}

const handlePromotionClick = () => {
  close()
  if (isPromotionSection.value) {
    // Pequeño delay para que el cierre del drawer no tape la apertura del panel
    nextTick(() => {
      openSubmenu()
    })
  } else {
    navigateTo('/promotions')
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="mobile-menu">
      <div v-if="isOpen" class="fixed inset-0 z-[9999] flex">
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="close"
        />

        <div
          ref="menuRef"
          class="relative w-80 max-w-[85vw] bg-white h-full flex flex-col shadow-2xl overflow-y-auto"
        >
          <div class="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-[#0f172a]">
            <div class="flex items-center gap-3">
              <div class="flex p-2 rounded bg-gray-700">
                <img src="/images/logo_small.png" :alt="appName" class="h-6">
              </div>
              <span class="text-white font-semibold text-lg">{{ appName }}</span>
            </div>
            <button
              @click="close"
              class="flex h-9 w-9 items-center justify-center rounded-full bg-gray-700 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white"
            >
              <Icon name="mdi:close" class="w-5 h-5" />
            </button>
          </div>

          <nav class="flex-1 p-4">
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Navegación
            </p>
            <div class="space-y-1">
              <template v-for="item in menuItems" :key="item.path">
                <button
                  v-if="item.isPromotionOverlay"
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                  :class="isMenuItemActive(item.path)
                    ? 'bg-slate-100 text-slate-900 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'"
                  @click="handlePromotionClick"
                >
                  <Icon :name="item.icon" class="text-xl" />
                  <span class="text-sm font-medium">{{ item.name }}</span>
                </button>

                <NuxtLink
                  v-else
                  :to="item.path"
                  class="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                  :class="isMenuItemActive(item.path)
                    ? 'bg-slate-100 text-slate-900 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'"
                >
                  <Icon :name="item.icon" class="text-xl" />
                  <span class="text-sm font-medium">{{ item.name }}</span>
                </NuxtLink>
              </template>
            </div>
          </nav>

          <div class="border-t border-gray-200 p-4">
            <UserMenu />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}

.mobile-menu-enter-active .relative,
.mobile-menu-leave-active .relative {
  transition: transform 0.3s ease;
}

.mobile-menu-enter-from .relative {
  transform: translateX(-100%);
}

.mobile-menu-leave-to .relative {
  transform: translateX(-100%);
}
</style>
